import { AttachmentIcon, DeleteIcon, DownloadIcon } from "@chakra-ui/icons";
import { Box, Button, Flex, IconButton } from "@chakra-ui/react";
import api from "api/api";
import DynamicTable from "components/dynamic-table";
import { Column } from "components/dynamic-table/types";
import { openModalFields } from "components/modal-fields";
import { openModalUploadFile } from "components/modal-upload-file";
import { Toast } from "components/toast";
import { UploadIcon } from "components/vectors/upload-icon";
import { useAttendanceContext } from "contexts/attendance-context";
import { useEffect, useState } from "react";

interface AnexoProps {
  id?: number;
  dataCriacao?: string;
  filename?: string;
  urlDownload?: string;
  description?: string;
}

export function AnexosAtendimentoPart({
  telaAtendimento,
}: {
  telaAtendimento: boolean;
}) {
  const { formControl, isReadOnly } = useAttendanceContext();
  const formValues = formControl.values;
  const [isLoading, setIsLoading] = useState<{
    delete: number[];
    upload: boolean;
    table: boolean;
  }>({
    delete: [],
    upload: false,
    table: false,
  });

  const [dados, setDados] = useState<AnexoProps[]>([]);

  const findAnexos = async ({ withLoading = false } = {}) => {
    if (withLoading) setIsLoading((prev) => ({ ...prev, table: true }));
    try {
      const { data } = await api.get(`/atendimentos/${formValues.id}/anexos`);
      setDados(data);
    } catch (e: any) {
      console.log(e.response);
    } finally {
      if (withLoading) setIsLoading((prev) => ({ ...prev, table: false }));
    }
  };

  const deleteAnexo = async (idAtendimentoAnexo: number) => {
    setIsLoading((prev) => ({
      ...prev,
      delete: [...prev.delete, idAtendimentoAnexo],
    }));
    try {
      await api.delete(`/atendimentos/${idAtendimentoAnexo}/delete-anexo`);
      Toast({
        title: "Arquivo deletado com sucesso",
        status: "success",
      });
      findAnexos();
    } catch (e: any) {
      Toast({
        title: "Erro ao deletar arquivo",
        description: e.response?.data?.msgInterface || "",
        status: "error",
      });
    } finally {
      setIsLoading((prev) => ({
        ...prev,
        delete: prev.delete.filter((c) => c !== idAtendimentoAnexo),
      }));
    }
  };

  const handleSubmitFile = async (file: File, description: string) => {
    setIsLoading((prev) => ({ ...prev, upload: true }));
    const formData = new FormData();
    formData.append("file", file!);
    formData.append("aceita_substituir", "false");
    formData.append("description", description);
    try {
      const { data } = await api.post(
        `/atendimentos/${formValues.id}/uploading/anexos`,
        formData
      );
      setDados([...dados, data]);
    } catch (e: any) {
      Toast({
        title: "Erro ao salvar arquivo",
        description: e.response?.data?.msgInterface || "",
        status: "error",
      });
    } finally {
      setIsLoading((prev) => ({ ...prev, upload: false }));
    }
  };

  useEffect(() => {
    findAnexos({ withLoading: true });
  }, []);

  const formatedName = (name: string) => {
    const limitDesc = telaAtendimento ? 120 : 20;
    return name.length > limitDesc
      ? name.slice(0, limitDesc).trim() + "..."
      : name;
  };

  const colums: Column<AnexoProps>[] = [
    {
      name: "Data Criação",
      key: "dataCriacao",
      render: (row) =>
        row.dataCriacao ? new Date(row.dataCriacao).toLocaleDateString() : null,
    },
    {
      name: "Nome Arquivo",
      key: "filename",
      render: (row) => formatedName(row.filename!),
    },
    {
      name: "Descrição",
      key: "description",
      render: (row) => row.description,
    },
    {
      name: "Ações",
      render: (row) => {
        return (
          <>
            <a href={row.urlDownload} target="_blank" rel="noreferrer">
              <IconButton
                aria-label="Download Anexo"
                icon={<DownloadIcon w="13px" h="13px" />}
                minW="25px"
                h="25px"
                p="0"
                mr="5px"
              />
            </a>
            <IconButton
              aria-label="Deletar Anexo"
              variant="danger"
              icon={<DeleteIcon w="13px" h="13px" />}
              minW="25px"
              h="25px"
              p="0"
              onClick={() => deleteAnexo(row.id!)}
              isLoading={isLoading.delete.includes(row.id!)}
            />
          </>
        );
      },
    },
  ];

  return (
    <Box>
      <Flex justifyContent="flex-end" mb="10px">
        <Button
          onClick={() => {
            openModalFields({
              paramsSettings: {
                params: [
                  { name: "Descrição:", field: "description" },
                  {
                    name: "Insira um arquivo:",
                    field: "file",
                    type: "inputFile",
                  },
                ],
              },
              onSubmit: async (data) => {
                await handleSubmitFile(data["file"], data["description"]);
              },
              simpleValidation: true,
              title: "Upload de arquivo",
              confirmButtonStyle: {
                children: "Upload",
                leftIcon: <UploadIcon width="20px" height="20px" />,
              },
            });
          }}
          leftIcon={<AttachmentIcon />}
          isDisabled={isReadOnly}
        >
          Anexar Documento
        </Button>
      </Flex>
      <DynamicTable
        columns={colums}
        rows={dados}
        sortBy="dataCriacao"
        sortOrder="DESC"
        emptyMessage="Não a nenhum documento anexado"
        isLoading={isLoading.table}
      />
    </Box>
  );
}
